import axios, { isCancel, type AxiosInstance } from "axios";
import { AuthContext } from "@/components/AuthProvider";
import { useCallback, useContext } from "react";
import { safeSessionStorage } from "@/lib/storage";
import { instrumentApiClient } from "@/lib/interactionTrace";
import { instrumentApiClientForRecording } from "@/components/codesmith/sessionRecording/sessionRecorder";
import { attachCsrf } from "@/lib/csrf";

const DEFAULT_TIMEOUT_MS = 60_000;

const clients = new Map<number, AxiosInstance>();

function getClient(timeoutMs: number): AxiosInstance {
  const existing = clients.get(timeoutMs);
  if (existing) return existing;

  const client = axios.create({
    baseURL: `${process.env.NEXT_PUBLIC_API_URL}/api`,
    timeout: timeoutMs,
  });
  instrumentApiClient(client);
  instrumentApiClientForRecording(client);

  client.interceptors.request.use(
    (config) => {
      config.withCredentials = true;
      return config;
    },
    (error) => {
      console.error(`Error on request: ${error}`);
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (isCancel(error)) return Promise.reject(error);
      console.error(`Error on response: ${error}`);
      if (error.response?.status === 401) {
        // TODO(aayush): Rationalize how we're going to show a re-login modal to the user
        // when their session expires due to inactivity.
        console.error("Unauthorized");
      }
      return Promise.reject(error);
    },
  );

  attachCsrf(client);

  clients.set(timeoutMs, client);
  return client;
}

/** The client `useApi` returns, for code outside React such as Storybook `beforeEach`. */
export function apiClient(timeout: number = DEFAULT_TIMEOUT_MS): AxiosInstance {
  return getClient(timeout);
}

// useApi is a hook that returns an axios instance that is already hydrated with the
// proper base URL and authentication headers.
export const useApi = (timeout?: number) => {
  // Session storage can change between renders without a React state update.
  "use no memo";

  const client = apiClient(timeout);

  // Keep Authorization on defaults (not only in an interceptor) so raw-fetch
  // helpers that read `api.defaults.headers.common.Authorization` stay in sync
  // when `fa-test-token` is set after mount.
  if (typeof window !== "undefined") {
    // The opaque-origin canvas sandbox frame throws on storage access.
    const token = safeSessionStorage.get("fa-test-token");
    if (token) {
      client.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete client.defaults.headers.common.Authorization;
    }
  }

  return client;
};

// Helper function for streaming fetch requests that need proper authorization headers
export const useStreamingApi = () => {
  const api = useApi();

  const streamingFetch = useCallback(
    async (url: string, options: RequestInit = {}) => {
      const authorization = api.defaults.headers.common.Authorization;
      const headers = new Headers({
        Authorization: typeof authorization === "string" ? authorization : "",
        Accept: "application/x-ndjson",
      });
      new Headers(options.headers).forEach((value, name) =>
        headers.set(name, value),
      );

      return fetch(`${api.defaults.baseURL}/${url.replace(/^\//, "")}`, {
        ...options,
        headers,
        credentials: "include",
      });
    },
    [api.defaults.baseURL, api.defaults.headers.common.Authorization],
  );

  return { streamingFetch };
};
